<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import type { RuleDTO } from '@/types'
import type { WorkspaceRuleListItemDTO } from '@/types/workspace'

import RuleFormDialog from '@/components/rules/RuleFormDialog.vue'
import RulesTable from '@/components/rules/RulesTable.vue'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import ruleService from '@/services/ruleService'

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const { t } = useI18n()
const toast = useAppToast()

// State
const rules = ref<RuleDTO[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)

// Dialog state
const showRuleDialog = ref(false)
const selectedRule = ref<RuleDTO | null>(null)

// Computed
const personalRuleListItems = computed<WorkspaceRuleListItemDTO[]>(() =>
  (rules.value ?? [])
    .filter((r) => !!r.userId)
    .map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description,
      eventCount: r.eventCount,
      groupCount: r.groupCount,
      restrictionCategories: getRestrictionCategories(r),
    })),
)

function getRestrictionCategories(rule: RuleDTO): string[] {
  if (rule.restrictionRelaxed) {
    return ['relaxed']
  }

  const categories: string[] = []

  if (rule.restrictionUserTypes?.length) {
    categories.push('userTypes')
  }
  if (rule.restrictionDegrees?.length) {
    categories.push('degrees')
  }
  if (rule.restrictionTermCounts?.length) {
    categories.push('termCounts')
  }
  if (rule.restrictionProducts?.length) {
    categories.push('products')
  }
  if (rule.restrictionBricks?.length && rule.restrictionBricksMode !== 'none') {
    categories.push('bricks')
  }
  if (rule.restrictionEvents?.length) {
    categories.push('events')
  }

  return categories
}

const sortedRules = computed<WorkspaceRuleListItemDTO[]>(() =>
  [...personalRuleListItems.value].sort((a, b) => b.id - a.id),
)

// Methods
async function loadRules() {
  isLoading.value = true
  error.value = null

  try {
    rules.value = await ruleService.getPersonalList()
  } catch (err) {
    console.error('Error loading rules:', err)
    error.value = err instanceof Error ? err.message : 'Failed to load rules'
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.rules.errorLoading'),
      duration: 3000,
    })
  } finally {
    isLoading.value = false
  }
}

function createRule() {
  selectedRule.value = null
  showRuleDialog.value = true
}

async function editRule(rule: WorkspaceRuleListItemDTO) {
  selectedRule.value = await ruleService.getById(rule.id)
  showRuleDialog.value = true
}

function onRuleSaved() {
  showRuleDialog.value = false
  selectedRule.value = null
  loadRules()
}

async function confirmDelete(rule: WorkspaceRuleListItemDTO) {
  // Check if rule is in use
  if (rule.eventCount > 0 || rule.groupCount > 0) {
    toast.add({
      color: 'warning',
      title: t('common.messages.warning'),
      description: t('features.rules.cannotDeleteInUse', {
        events: rule.eventCount,
        groups: rule.groupCount,
      }),
      duration: 5000,
    })
    return
  }

  const accepted = await appConfirm({
    message: t('rules.confirmDelete', { name: rule.name }),
    title: t('common.labels.confirmDeletion'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.delete'),
    confirmColor: 'error',
  })
  if (accepted) await deleteRule(rule.id)
}

async function deleteRule(id: number) {
  try {
    await ruleService.delete(id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('features.rules.deleteSuccess'),
      duration: 3000,
    })
    await loadRules()
  } catch (err) {
    console.error('Error deleting rule:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.rules.deleteError'),
      duration: 3000,
    })
  }
}

// Lifecycle
onMounted(() => {
  loadRules()
})
</script>

<template>
  <div class="rules-page">
    <!-- Header with Create Button -->
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ t('features.rules.title') }}
        </h2>
        <p class="mt-1 text-gray-600 dark:text-gray-300">
          {{ t('features.rules.description') }}
        </p>
      </div>
      <UButton
        icon="i-lucide-plus"
        :label="t('features.rules.createNew')"
        color="neutral"
        @click="createRule"
      />
    </div>

    <AppAlert v-if="error" color="error" :closable="false" class="mb-4">
      {{ error }}
    </AppAlert>

    <RulesTable
      :rules="sortedRules"
      :loading="isLoading"
      @edit="editRule"
      @delete="confirmDelete"
    />

    <!-- Create/Edit Rule Dialog -->
    <RuleFormDialog
      :visible="showRuleDialog"
      :rule="selectedRule"
      @update:visible="showRuleDialog = $event"
      @success="onRuleSaved"
    />
  </div>
</template>

<style scoped>
.rules-page {
  padding: 1.5rem;
}
</style>
